import { useContext, useEffect, useRef, useState } from "react";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import { useIsInView } from "common/LazyInView";
import useDebouncer from "common/hooks/debouncer";
import { pollWhileShown } from "./utils";

// The data of a card of the dashboard, retrieved the way every one of them
// retrieves it. Nothing is asked while the BQN context has not loaded the
// BQN selected, whose option the commands carry, since the responses would
// be those of the whole cluster, nor while the card is out of view, since
// what came back would be drawn for nobody: the commands of the GUI are run
// one after the other, so it would also take a turn away from the cards that
// are looked at. The retrieval happens again when the BQN comes, when the
// card comes back into view, and for each of the values it is told to watch.
//
// A card is clocked in one of two ways, which the interval tells apart:
//
// - Given an interval, the card asks for its data on a cadence of its own
//   while the page is shown, and on each refresh of the dashboard.
// - Given none, the range of the dashboard is its clock: the page reloads it
//   on a change of the period or of the BQN, on an interval and on a
//   refresh, so a card that draws the range asks for nothing of its own and
//   is retrieved again with each one of them. Nothing is asked until the
//   page gives the first range.
//
// The retrieval is given the BQN selected and the range, and answers what
// the card draws. An error of it is logged under the message given, and the
// card is left with the data of the last answer, if there is one.
//
// The commands of a retrieval are queued under a scope of the card, and the
// ones not sent yet are dropped when the retrieval is stopped: the commands
// of a card that was left, or of a period that was changed, would otherwise
// keep the queue of the CLI busy for nobody, and the next view, or the next
// period, would wait behind them.
//
// RETURN VALUE:
//   The data of the last answer, or null while there is none, and whether
//   the card is loading: it is until the first answer, and again from a
//   change of the BQN or of the values it is told to watch until the answer
//   for them, since what it drew is of the old ones.
const useDashboardData = ({
  retrieve,
  onError,
  deps = [],
  interval = undefined,
  range = undefined,
}) => {
  const actions = useContext(ActionsContext);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const debounce = useDebouncer(500 /*ms*/);
  const isInView = useIsInView();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // The retrieval of the last render, which is the one that knows what the
  // card is drawing: the effect below runs again for the values it is told
  // to watch, and not for a function that is a new one on every render.
  const retrieveLast = useRef(retrieve);

  retrieveLast.current = retrieve;

  const isRangeClocked = interval === undefined;

  // A change of the BQN or of the values watched is a new load, unlike a new
  // range or a refresh, which redraw what is shown once the answer comes.
  useEffect(() => {
    setIsLoading(true);
  }, [bqnId, mode, ...deps]);

  useEffect(() => {
    if (
      bqnId === undefined ||
      !isInView ||
      (isRangeClocked && range === undefined)
    ) {
      return;
    }

    let active = true;
    const scope = {};

    const take = (dataRetrieved) => {
      if (active) {
        setData(dataRetrieved);
        setIsLoading(false);
      }
    };

    // The card keeps what it drew of the last answer, and stops waiting for
    // the first one: an error of its own does not stop the retrievals, which
    // the BQN may answer again. An error of a retrieval that was stopped is
    // nobody's, such as the one of a command dropped from the queue.
    const handleError = (error) => {
      if (active) {
        console.error(onError, error);
        setIsLoading(false);
      }

      return true;
    };

    const retrieveOnce = () =>
      ifCl.runScoped(scope, () =>
        retrieveLast.current({ mode, bqnId, range })
      );

    if (isRangeClocked) {
      retrieveOnce().then(take).catch(handleError);

      return () => {
        active = false;
        ifCl.dropQueued(scope);
      };
    }

    const stop = pollWhileShown({
      retrieve: retrieveOnce,
      take,
      handleError,
      interval,
    });
    const unsubscribe = actions.recv("do-load", () =>
      debounce(() => retrieveOnce().then(take).catch(handleError))
    );

    return () => {
      active = false;
      stop();
      unsubscribe();
      debounce.stop();
      ifCl.dropQueued(scope);
    };
  }, [
    bqnId,
    mode,
    isInView,
    isRangeClocked,
    interval,
    range,
    onError,
    actions,
    debounce,
    ...deps,
  ]);

  return { data, isLoading };
};

export default useDashboardData;
